import React, { createContext, useContext, useEffect, useState } from "react";
import { useSelector, useDispatch } from "react-redux";

import { createAppSocket, emitAgentSetup } from "./api/createAppSocket";
import {
  notificationReceivedFromSocket,
  NOTIFICATION_SOCKET_EVENTS,
} from "./store/features/notifications";

const SocketContext = createContext(null);

export const SocketProvider = ({ children }) => {
  const dispatch = useDispatch();
  const userData = useSelector((state) => state.loginToken);
  const [socket, setSocket] = useState(null);

  useEffect(() => {
    if (!userData?.response?.user?._id) {
      setSocket((prev) => {
        if (prev) {
          prev.disconnect();
        }
        return null;
      });
      return undefined;
    }

    const s = createAppSocket({
      reconnection: true,
      reconnectionAttempts: Infinity,
      reconnectionDelay: 500,
      reconnectionDelayMax: 15000,
      timeout: 25000,
    });

    const emitJoin = () => {
      emitAgentSetup(s);
      if (userData.response.user.business_id) {
        s.emit("join_business", {
          businessId: userData.response.user.business_id,
        });
      }
      // Keep this device in its session room across reconnects (remote logout).
      if (userData.response.sessionId) {
        s.emit("join-session", { sessionId: userData.response.sessionId });
      }
    };

    const onServerConnected = () => {
      console.log("✅ Socket Connected Global");
    };

    const onConnect = () => {
      console.log("Socket connected (SocketContext)");
      emitJoin();
    };

    const onDisconnect = (reason) => {
      console.warn("Socket disconnected:", reason);
    };

    const onReconnect = (attempt) => {
      console.log("Reconnected after", attempt, "attempts");
      emitJoin();
    };

    const onReconnectFailed = () => {
      console.warn("Socket reconnection failed; open new connection if idle");
    };

    s.on("connected", onServerConnected);
    s.on("connect", onConnect);
    s.on("disconnect", onDisconnect);
    s.on("reconnect", onReconnect);
    s.on("reconnect_failed", onReconnectFailed);

    emitJoin();

    setSocket(s);

    const handleVisibilityChange = () => {
      if (document.visibilityState !== "visible") return;
      if (!s.connected) {
        console.log("Page became visible - reconnecting socket...");
        s.connect();
        return;
      }
      emitJoin();
    };

    const handleBrowserOnline = () => {
      if (!s.connected) {
        console.log("Browser online - reconnecting socket...");
        s.connect();
      } else {
        emitJoin();
      }
    };

    document.addEventListener("visibilitychange", handleVisibilityChange);
    window.addEventListener("online", handleBrowserOnline);

    return () => {
      document.removeEventListener("visibilitychange", handleVisibilityChange);
      window.removeEventListener("online", handleBrowserOnline);
      s.off("connected", onServerConnected);
      s.off("connect", onConnect);
      s.off("disconnect", onDisconnect);
      s.off("reconnect", onReconnect);
      s.off("reconnect_failed", onReconnectFailed);
      s.disconnect();
      setSocket(null);
    };
  }, [
    userData?.response?.user?._id,
    userData?.response?.user?.business_id,
    userData?.response?.sessionId,
  ]);

  useEffect(() => {
    if (!userData?.response?.user?._id || !socket?.on) {
      return undefined;
    }

    const handler = (payload) => {
      dispatch(notificationReceivedFromSocket(payload));
    };

    NOTIFICATION_SOCKET_EVENTS.forEach((ev) => socket.on(ev, handler));
    return () => {
      NOTIFICATION_SOCKET_EVENTS.forEach((ev) => socket.off(ev, handler));
    };
  }, [userData?.response?.user?._id, dispatch, socket]);

  return (
    <SocketContext.Provider value={socket}>{children}</SocketContext.Provider>
  );
};

export const useSocket = () => {
  const context = useContext(SocketContext);
  if (context === undefined) {
    throw new Error("useSocket must be used within a SocketProvider");
  }
  return context;
};
